import { useRef } from 'react';
import SEOMetadata from '@/components/SEOMetadata';
import TrinidadTobagoHeader from '@/components/trinidad-tobago/TrinidadTobagoHeader';
import TTServicesGrid from '@/components/trinidad-tobago/TTServicesGrid';
import TTVPSPricingSection from '@/components/trinidad-tobago/TTVPSPricingSection';
import TTRDPPricingSection from '@/components/trinidad-tobago/TTRDPPricingSection';
import TTProxyPricingSection from '@/components/trinidad-tobago/TTProxyPricingSection';
import TrinidadTobagoFooter from '@/components/trinidad-tobago/TrinidadTobagoFooter';
import FreeProxyPopup, { FreeProxyPopupRef } from '@/components/FreeProxyPopup';

const TrinidadTobagoSplash = () => {
  const freeProxyPopupRef = useRef<FreeProxyPopupRef>(null);

  const handleGetFreeAccess = () => {
    freeProxyPopupRef.current?.openPopup();
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "US Residential Internet Services for Trinidad and Tobago",
    "description": "Access US residential proxies, VPNs, and remote desktops with TTD pricing. No USD required - we accept Trinidad and Tobago dollars.",
    "provider": {
      "@type": "Organization",
      "name": "NEXX TELECOM LLC"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Trinidad and Tobago"
    },
    "offers": {
      "@type": "Offer",
      "description": "Forex-friendly internet solutions accepting TTD payments"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 relative overflow-hidden">
      <SEOMetadata 
        title="US Internet Services for Trinidad & Tobago - Pay in TTD | NEXX"
        description="No USD? No problem! Access US residential proxies, VPNs, and remote desktops with Trinidad & Tobago dollar pricing. Bypass forex issues with our TTD payment options."
        keywords="Trinidad Tobago internet services, TTD payment, Caribbean VPS, forex friendly hosting, US proxies TTD, residential VPN Trinidad"
        canonical="https://nexx.net/trinidad-tobago"
        structuredData={structuredData}
      />

      {/* Flag-colored top border */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-600 via-white to-black z-30"></div>

      {/* Enhanced Animated Background */}
      <div className="absolute inset-0 overflow-hidden z-10">
        {/* Floating geometric shapes in flag colors */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-600/10 rounded-full animate-pulse" style={{ animationDelay: '0.5s', animationDuration: '4s' }}></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-white/20 rounded-lg rotate-45 animate-bounce border border-red-600/20" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
        <div className="absolute bottom-40 left-20 w-20 h-20 bg-black/10 rounded-full animate-ping" style={{ animationDelay: '1.5s', animationDuration: '4s' }}></div>
        <div className="absolute bottom-20 right-40 w-16 h-16 bg-red-600/15 rounded-lg animate-pulse" style={{ animationDelay: '2s', animationDuration: '3s' }}></div>
        <div className="absolute top-1/3 left-1/4 w-8 h-8 bg-white/30 rounded-full animate-bounce border border-black/20" style={{ animationDelay: '0.8s', animationDuration: '5s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-12 h-12 bg-black/5 rounded-lg rotate-12 animate-pulse" style={{ animationDelay: '2.5s', animationDuration: '3.5s' }}></div>
        
        {/* Small floating dots */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-red-600/20 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 flex flex-col min-h-screen">
        <div className="flex-grow flex flex-col justify-center p-4 max-w-7xl mx-auto w-full">
          <TrinidadTobagoHeader />
          <TTServicesGrid onGetFreeAccess={handleGetFreeAccess} />
          <TTVPSPricingSection />
          <TTRDPPricingSection />
          <TTProxyPricingSection />
        </div>
        <TrinidadTobagoFooter />
      </div>

      <FreeProxyPopup ref={freeProxyPopupRef} />
    </div>
  );
};

export default TrinidadTobagoSplash;